import React from 'react';

// MoveMend brand colors
const brandColors = {
  primary: '#6d4f79',
  secondary: '#8b6897',
  accent: '#a584b3',
  light: '#c3a9ce',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  gray: '#9ca3af'
};

// Line Chart Component for ROM Trends
export const LineChart = ({ data, width = 500, height = 250 }) => {
  const padding = 40;
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;

  // Find min and max values for scaling
  const allValues = data.flatMap(d => [d.shoulder, d.elbow, d.hip].filter(v => v !== undefined));
  const minValue = Math.min(...allValues) - 10;
  const maxValue = Math.max(...allValues) + 10;

  // Scale functions
  const xScale = (index) => (index / (data.length - 1)) * chartWidth + padding;
  const yScale = (val) => height - padding - ((val - minValue) / (maxValue - minValue)) * chartHeight;

  // Create line paths
  const createPath = (key, color) => {
    const points = data
      .map((d, i) => d[key] !== undefined ? `${xScale(i)},${yScale(d[key])}` : null)
      .filter(p => p !== null)
      .join(' L ');
    return points ? `M ${points}` : '';
  };

  return (
    <svg width={width} height={height} style={{ fontFamily: 'inherit' }}>
      {/* Grid lines */}
      {[0, 1, 2, 3, 4].map(i => {
        const y = padding + (chartHeight / 4) * i;
        const value = Math.round(maxValue - ((maxValue - minValue) / 4) * i);
        return (
          <g key={i}>
            <line
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeDasharray="2,2"
            />
            <text x={padding - 10} y={y + 5} textAnchor="end" fontSize="12" fill="#6b7280">
              {value}°
            </text>
          </g>
        );
      })}

      {/* X-axis labels */}
      {data.map((d, i) => (
        <text
          key={i}
          x={xScale(i)}
          y={height - padding + 20}
          textAnchor="middle"
          fontSize="12"
          fill="#6b7280"
        >
          {d.week}
        </text>
      ))}

      {/* Lines */}
      <path
        d={createPath('shoulder', brandColors.primary)}
        fill="none"
        stroke={brandColors.primary}
        strokeWidth="2"
      />
      <path
        d={createPath('elbow', brandColors.secondary)}
        fill="none"
        stroke={brandColors.secondary}
        strokeWidth="2"
      />
      <path
        d={createPath('hip', brandColors.accent)}
        fill="none"
        stroke={brandColors.accent}
        strokeWidth="2"
      />

      {/* Data points */}
      {data.map((d, i) => (
        <g key={i}>
          {d.shoulder && (
            <circle cx={xScale(i)} cy={yScale(d.shoulder)} r="4" fill={brandColors.primary} />
          )}
          {d.elbow && (
            <circle cx={xScale(i)} cy={yScale(d.elbow)} r="4" fill={brandColors.secondary} />
          )}
          {d.hip && (
            <circle cx={xScale(i)} cy={yScale(d.hip)} r="4" fill={brandColors.accent} />
          )}
        </g>
      ))}

      {/* Legend */}
      <g transform={`translate(${width - 120}, 20)`}>
        <rect x="0" y="0" width="15" height="3" fill={brandColors.primary} />
        <text x="20" y="3" fontSize="12" fill="#374151">Shoulder</text>

        <rect x="0" y="15" width="15" height="3" fill={brandColors.secondary} />
        <text x="20" y="18" fontSize="12" fill="#374151">Elbow</text>

        <rect x="0" y="30" width="15" height="3" fill={brandColors.accent} />
        <text x="20" y="33" fontSize="12" fill="#374151">Hip</text>
      </g>
    </svg>
  );
};

// Area Chart Component for Form Scores
export const AreaChart = ({ data, width = 500, height = 250 }) => {
  const padding = 40;
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;

  const maxValue = 100;
  const xScale = (index) => (index / (data.length - 1)) * chartWidth + padding;
  const yScale = (val) => height - padding - (val / maxValue) * chartHeight;

  // Create area path
  const areaPath = data
    .map((d, i) => `${xScale(i)},${yScale(d.score)}`)
    .join(' L ');
  const fullAreaPath = `M ${areaPath} L ${xScale(data.length - 1)},${height - padding} L ${padding},${height - padding} Z`;

  return (
    <svg width={width} height={height} style={{ fontFamily: 'inherit' }}>
      {/* Grid lines */}
      {[0, 25, 50, 75, 100].map(val => {
        const y = yScale(val);
        return (
          <g key={val}>
            <line
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeDasharray="2,2"
            />
            <text x={padding - 10} y={y + 5} textAnchor="end" fontSize="12" fill="#6b7280">
              {val}%
            </text>
          </g>
        );
      })}

      {/* Area fill */}
      <path
        d={fullAreaPath}
        fill={brandColors.light}
        opacity="0.3"
      />

      {/* Line */}
      <path
        d={`M ${areaPath}`}
        fill="none"
        stroke={brandColors.primary}
        strokeWidth="2"
      />

      {/* Data points */}
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={xScale(i)} cy={yScale(d.score)} r="4" fill={brandColors.primary} />
          <text
            x={xScale(i)}
            y={height - padding + 20}
            textAnchor="middle"
            fontSize="12"
            fill="#6b7280"
          >
            {d.day}
          </text>
        </g>
      ))}
    </svg>
  );
};

// Bar Chart Component
export const BarChart = ({ data, width = 500, height = 200 }) => {
  const padding = 40;
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;
  const barWidth = chartWidth / (data.length * 3 + 1);
  const groupWidth = barWidth * 3;

  const maxValue = Math.max(...data.flatMap(d => [d.formScore, d.shoulderROM, d.improvement]));

  const xScale = (index) => padding + (index * (groupWidth + barWidth));
  const yScale = (val) => (val / maxValue) * chartHeight;

  return (
    <svg width={width} height={height} style={{ fontFamily: 'inherit' }}>
      {/* Y-axis grid lines */}
      {[0, 25, 50, 75, 100].map(val => {
        const y = height - padding - (val / 100) * chartHeight;
        return (
          <line
            key={val}
            x1={padding}
            y1={y}
            x2={width - padding}
            y2={y}
            stroke="#e5e7eb"
            strokeDasharray="2,2"
          />
        );
      })}

      {/* Bars */}
      {data.map((d, i) => {
        const x = xScale(i);
        return (
          <g key={i}>
            {/* Form Score Bar */}
            <rect
              x={x}
              y={height - padding - yScale(d.formScore)}
              width={barWidth}
              height={yScale(d.formScore)}
              fill={brandColors.primary}
            />
            <text
              x={x + barWidth / 2}
              y={height - padding - yScale(d.formScore) - 5}
              textAnchor="middle"
              fontSize="10"
              fill="#374151"
            >
              {d.formScore}%
            </text>

            {/* Shoulder ROM Bar */}
            <rect
              x={x + barWidth}
              y={height - padding - yScale(d.shoulderROM / 2)}
              width={barWidth}
              height={yScale(d.shoulderROM / 2)}
              fill={brandColors.secondary}
            />
            <text
              x={x + barWidth * 1.5}
              y={height - padding - yScale(d.shoulderROM / 2) - 5}
              textAnchor="middle"
              fontSize="10"
              fill="#374151"
            >
              {d.shoulderROM}°
            </text>

            {/* Improvement Bar */}
            <rect
              x={x + barWidth * 2}
              y={height - padding - yScale(d.improvement)}
              width={barWidth}
              height={yScale(d.improvement)}
              fill={brandColors.success}
            />
            <text
              x={x + barWidth * 2.5}
              y={height - padding - yScale(d.improvement) - 5}
              textAnchor="middle"
              fontSize="10"
              fill="#374151"
            >
              +{d.improvement}%
            </text>

            {/* X-axis label */}
            <text
              x={x + groupWidth / 2}
              y={height - padding + 20}
              textAnchor="middle"
              fontSize="12"
              fill="#6b7280"
            >
              {d.name}
            </text>
          </g>
        );
      })}

      {/* Legend */}
      <g transform={`translate(${width - 150}, 20)`}>
        <rect x="0" y="0" width="12" height="12" fill={brandColors.primary} />
        <text x="16" y="10" fontSize="11" fill="#374151">Form Score</text>

        <rect x="0" y="16" width="12" height="12" fill={brandColors.secondary} />
        <text x="16" y="26" fontSize="11" fill="#374151">ROM</text>

        <rect x="0" y="32" width="12" height="12" fill={brandColors.success} />
        <text x="16" y="42" fontSize="11" fill="#374151">Improvement</text>
      </g>
    </svg>
  );
};

// Pie Chart Component
export const PieChart = ({ data, width = 300, height = 200 }) => {
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 3;

  const total = data.reduce((sum, d) => sum + d.value, 0);
  let currentAngle = -Math.PI / 2;

  const slices = data.map(d => {
    const startAngle = currentAngle;
    const angle = (d.value / total) * 2 * Math.PI;
    currentAngle += angle;

    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(currentAngle);
    const y2 = centerY + radius * Math.sin(currentAngle);

    const largeArc = angle > Math.PI ? 1 : 0;

    const pathData = [
      `M ${centerX} ${centerY}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z'
    ].join(' ');

    // Label position
    const labelAngle = startAngle + angle / 2;
    const labelRadius = radius * 0.7;
    const labelX = centerX + labelRadius * Math.cos(labelAngle);
    const labelY = centerY + labelRadius * Math.sin(labelAngle);

    return {
      pathData,
      color: d.color,
      labelX,
      labelY,
      name: d.name,
      value: d.value
    };
  });

  return (
    <svg width={width} height={height} style={{ fontFamily: 'inherit' }}>
      {slices.map((slice, i) => (
        <g key={i}>
          <path
            d={slice.pathData}
            fill={slice.color}
            stroke="white"
            strokeWidth="2"
          />
          <text
            x={slice.labelX}
            y={slice.labelY}
            textAnchor="middle"
            fontSize="12"
            fill="white"
            fontWeight="bold"
          >
            {slice.value}%
          </text>
        </g>
      ))}

      {/* Legend */}
      <g transform={`translate(${width - 100}, 20)`}>
        {data.map((d, i) => (
          <g key={i} transform={`translate(0, ${i * 20})`}>
            <rect x="0" y="0" width="12" height="12" fill={d.color} />
            <text x="16" y="10" fontSize="11" fill="#374151">{d.name}</text>
          </g>
        ))}
      </g>
    </svg>
  );
};